import React, { useState } from 'react';
import Modal from '@/components/ui/Modal';
import { Button, Input, useToast } from '@/components/ui';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  campaignsService,
  type Campaign,
  type TriggerAiCallRequest,
} from '@/services/api/campaigns';
import {
  CheckCircle2,
  FileText,
  Loader2,
  Phone,
  PhoneCall,
  User,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
}

type CallStatus = 'idle' | 'calling' | 'success';

const TriggerAiCallModal: React.FC<Props> = ({ isOpen, onClose, campaign }) => {
  const { success, error: toastError } = useToast();
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [callResult, setCallResult] = useState<any>(null);

  const formik = useFormik<Omit<TriggerAiCallRequest, 'campaign_uuid'>>({
    initialValues: {
      phone_number: '',
      lead_name: '',
      notes: '',
    },
    validationSchema: Yup.object({
      phone_number: Yup.string()
        .required('Phone number is required')
        .matches(/^[+\d\s\-()]{7,}$/, 'Enter a valid phone number'),
      lead_name: Yup.string(),
      notes: Yup.string(),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!campaign) return;
      setCallStatus('calling');
      try {
        const res = await campaignsService.triggerAiCall({
          campaign_uuid: campaign.uuid,
          ...values,
        });
        setCallResult(res?.data ?? {});
        setCallStatus('success');
        success(`AI call initiated to ${values.phone_number}!`);
      } catch (e: any) {
        setCallStatus('idle');
        toastError(e?.message || 'Failed to trigger AI call');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setCallStatus('idle');
    setCallResult(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        callStatus === 'success' ? 'Call Initiated!' : 'Trigger AI Agent Call'
      }
      size="md"
      footer={
        callStatus === 'success' ? (
          <div className="flex justify-end px-2">
            <Button
              onClick={handleClose}
              className="bg-primary-button hover:bg-primary-button text-white"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-3 px-2">
            <Button
              type="submit"
              form="trigger-ai-call-form"
              disabled={formik.isSubmitting || callStatus === 'calling'}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
            >
              {callStatus === 'calling' ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Connecting…
                </>
              ) : (
                <>
                  <PhoneCall size={15} />
                  Start AI Call
                </>
              )}
            </Button>
            <Button variant="outline" type="button" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        )
      }
    >
      {/* ── Success State ──────────────────────────────────────────────────── */}
      {callStatus === 'success' ? (
        <div className="px-6 py-8 flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 size={36} className="text-green-500" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Call Initiated!</h3>
            <p className="text-gray-500 text-sm mt-1">
              The AI agent is now calling{' '}
              <span className="font-semibold text-gray-700">
                {callResult?.phone_number}
              </span>
            </p>
          </div>

          {/* Call details */}
          <div className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-left space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Call ID</span>
              <span className="font-mono text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                {callResult?.call_id}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                {callResult?.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Initiated at</span>
              <span className="text-gray-700">
                {callResult?.initiated_at
                  ? new Date(callResult.initiated_at).toLocaleTimeString()
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <form id="trigger-ai-call-form" onSubmit={formik.handleSubmit} className="px-4 py-4 space-y-5">
          {/* Campaign banner */}
          {campaign && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
              <p className="text-xs text-indigo-500 font-semibold uppercase tracking-wide mb-0.5">Campaign</p>
              <p className="font-bold text-indigo-900">{campaign.name}</p>
              {campaign.description && (
                <p className="text-indigo-600 text-xs mt-0.5 line-clamp-2">{campaign.description}</p>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {campaign.lead_categories.map((cat) => (
                  <span
                    key={cat}
                    className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700 font-medium capitalize"
                  >
                    {cat.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Phone Number */}
          <div>
            <label className="text-xs font-semibold mb-1 block text-gray-700 flex items-center gap-1">
              <Phone size={12} className="text-gray-400" />
              Phone Number <span className="text-red-500">*</span>
            </label>
            <Input
              name="phone_number"
              type="tel"
              value={formik.values.phone_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="+1 (555) 000-0000"
            />
            {formik.touched.phone_number && formik.errors.phone_number && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.phone_number}</p>
            )}
          </div>

          {/* Lead Name */}
          <div>
            <label className="text-xs font-semibold mb-1 block text-gray-700 flex items-center gap-1">
              <User size={12} className="text-gray-400" />
              Lead Name <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <Input
              name="lead_name"
              value={formik.values.lead_name ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g. John Doe"
            />
          </div>

          {/* Script preview */}
          {campaign && campaign.script.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                <FileText size={12} />
                Script Preview ({campaign.script.length} question{campaign.script.length !== 1 ? 's' : ''})
              </p>
              <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                {campaign.script.map((q, idx) => (
                  <div key={q.id} className="px-3 py-2 bg-gray-50 flex items-start gap-2">
                    <span className="text-xs font-bold text-indigo-400 shrink-0 mt-0.5">
                      Q{idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-xs text-gray-800">{q.question}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-400 capitalize bg-gray-100 px-1.5 py-0.5 rounded">
                          {q.type.replace('_', ' ')}
                        </span>
                        {q.required && (
                          <span className="text-[10px] text-red-400">Required</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold mb-1 block text-gray-700">
              Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              name="notes"
              value={formik.values.notes ?? ''}
              onChange={formik.handleChange}
              rows={2}
              placeholder="Any specific instructions for this call…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {/* Calling animation hint */}
          {callStatus === 'calling' && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Loader2 size={18} className="text-green-500 animate-spin shrink-0" />
              <p className="text-sm text-green-700">
                Connecting AI agent to{' '}
                <strong>{formik.values.phone_number}</strong>…
              </p>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
};

export default TriggerAiCallModal;
